import DOMPurify from 'isomorphic-dompurify';
import { findHtmlQuoteSplit } from '../lib/htmlQuote.js';

/** Разрезать HTML-тело на свежую часть и цитируемую историю (null если границы нет). */
export const splitHtmlQuote = (html) => {
    const i = findHtmlQuoteSplit(html);
    if (i <= 0) return { main: html, quote: null };
    return { main: html.slice(0, i), quote: html.slice(i) };
};

/**
 * Split email body into main content and the quoted/forwarded portion.
 * Returns { main, quoted } where quoted may be null.
 */
export const parseEmailBody = (text) => {
    if (!text) return { main: '', quoted: null };

    const lines = text.split(/\r?\n/);
    let splitAt = lines.length;

    const QUOTE_STARTERS = [
        /^>/,
        // Russian day-of-week attribution: "пт, 6 мар. 2026 г. в 16:13, Nikita Digital <...>:"
        /^(вт,|ср,|чт,|пт,|пн,|сб,|вс,)\s+\d/i,
        /^(on |вт,|ср,|чт,|пт,|пн,|сб,|вс,).+wrote:/i,
        /^(on |вт,|ср,|чт,|пт,|пн,|сб,|вс,).+написал/i,
        // English date: "On Wednesday, Mar 11, 2026 at 7:56 am ... wrote:"
        /^on\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)/i,
        // Russian numeric date attribution: "6 мар. 2026 г., 14:00, @nixoraketa:"
        /^\d{1,2}\s+(янв|фев|мар|апр|мая|июн|июл|авг|сен|окт|ноя|дек)/i,
        /^from:\s/i,
        /^от:\s/i,
        /^-{4,}/,
        /^_{4,}/,
    ];

    for (let i = 0; i < lines.length; i++) {
        const trimmed = lines[i].trim();
        if (QUOTE_STARTERS.some(p => p.test(trimmed))) {
            splitAt = i;
            break;
        }
    }

    const main = lines.slice(0, splitAt).join('\n').trim();
    const quoted = splitAt < lines.length ? lines.slice(splitAt).join('\n').trim() : null;
    return { main: main || text.trim(), quoted };
};

/**
 * Split quoted block into:
 *  - header: the date/sender attribution line(s) (no > prefix)
 *  - content: the actual quoted text with > stripped
 */
export const parseQuotedBlock = (quoted) => {
    if (!quoted) return { header: null, content: null };

    const lines = quoted.split(/\r?\n/);

    // First non-empty line is the attribution header (date/sender line)
    let headerLine = null;
    let contentStartIdx = 0;

    for (let i = 0; i < lines.length; i++) {
        const trimmed = lines[i].trim();
        if (!trimmed) continue;
        // First non-empty line is always the header
        headerLine = trimmed;
        contentStartIdx = i + 1;
        break;
    }

    // Everything after the header is content — strip > prefixes
    const contentLines = lines.slice(contentStartIdx).map(l => l.replace(/^>+\s?/, ''));
    const content = contentLines.join('\n').trim() || null;

    return { header: headerLine, content };
};

/**
 * Extract the first meaningful content line from the quoted text (for preview in toggle).
 */
export const getQuoteFirstLine = (content) => {
    if (!content) return null;
    for (const line of content.split(/\r?\n/)) {
        const trimmed = line.trim();
        if (trimmed) return trimmed;
    }
    return null;
};

/** Detect whether a string contains HTML tags. */
export const hasHtml = (str) => /<[a-z][^>]*>/i.test(str || '');

// Allow-list sanitizer config (C1 leg a). Layout/media/formatting tags used by
// real Reply.io / operator-template email HTML; everything else (script, style,
// iframe, object, embed, svg, form, event handlers) is stripped by DOMPurify.
const SANITIZE_ALLOWED_TAGS = [
    'a', 'b', 'i', 'em', 'strong', 'p', 'div', 'span', 'br',
    'ul', 'ol', 'li', 'blockquote', 'img',
    'table', 'thead', 'tbody', 'tr', 'td', 'th',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'pre', 'code', 'hr', 'font',
];
const SANITIZE_ALLOWED_ATTR = [
    'href', 'src', 'alt', 'title', 'style',
    'width', 'height', 'align', 'target', 'rel',
    'colspan', 'rowspan',
];
// URI schemes real inbound/template emails use: absolute http(s) (app uploads are
// served at an absolute https URL — see offerEmail.js / uploads.js), mailto/tel
// links, cid: inline-image refs, protocol-relative `//host`, and relative/anchor
// paths (all matched by the `[^a-z]` / trailing-non-scheme branches). Mirrors the
// DOMPurify default shape but NEVER admits `javascript:` or `data:` (both fail the
// scheme list and the `[a-z+.\-]+:` branch that excludes a trailing colon).
const SANITIZE_ALLOWED_URI_REGEXP =
    /^(?:(?:https?|mailto|tel|cid):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i;
// CSS that turns a kept `style` attr into an XSS / exfiltration vector.
const SANITIZE_STYLE_BLOCKLIST =
    /url\(|@import|expression\(|behavior|-moz-binding|position\s*:\s*fixed/i;

DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    // target=_blank links must not leak window.opener to the destination.
    if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
        node.setAttribute('rel', 'noopener noreferrer');
    }
    // Keep inline layout styles, but drop the whole attr if it smuggles a
    // fetch/exec/overlay primitive (DOMPurify allows the attr, it does not police it).
    if (node.hasAttribute && node.hasAttribute('style')) {
        if (SANITIZE_STYLE_BLOCKLIST.test(node.getAttribute('style') || '')) {
            node.removeAttribute('style');
        }
    }
});

/**
 * Allow-list HTML sanitizer for every email/template render sink.
 * Strips script/style/iframe/object/embed/svg + all `on*` handlers, neutralizes
 * `javascript:`/`data:` URIs, and CSS-polices the kept inline `style` attr.
 * @param {string} html
 * @returns {string}
 */
export const sanitizeHtml = (html) =>
    DOMPurify.sanitize(html || '', {
        ALLOWED_TAGS: SANITIZE_ALLOWED_TAGS,
        ALLOWED_ATTR: SANITIZE_ALLOWED_ATTR,
        ALLOW_DATA_ATTR: false,
        ALLOWED_URI_REGEXP: SANITIZE_ALLOWED_URI_REGEXP,
    });

/**
 * If bodyText starts with the subject string (legacy Instantly webhook quirk preserved
 * for historical LeadMessage rows), strip it to avoid "Re: Re:" duplication.
 */
export const stripLeadingSubject = (body, subject) => {
    if (!subject || !body) return body;
    const trimmed = body.trimStart();
    if (trimmed.toLowerCase().startsWith(subject.toLowerCase())) {
        return trimmed.slice(subject.length).replace(/^[\r\n]+/, '');
    }
    return body;
};
